'use client';

import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { browserApiRequest } from '@/shared/api';

import {
  cartKeys,
  CartSchema,
  type AddCartLinesCommand,
  type Cart,
  type UpdateCartLineCommand,
} from '../model/cart';

export function cartQueryOptions() {
  return queryOptions({
    queryKey: cartKeys.current(),
    queryFn: ({ signal }) => browserApiRequest('/api/cart', CartSchema, { signal }),
    staleTime: 15_000,
  });
}

const json = (body: unknown) => JSON.stringify(body);
const linePath = (lineId: string) => `/api/cart/lines/${encodeURIComponent(lineId)}` as const;

function useCartMutation<TInput>(key: string, request: (input: TInput) => Promise<Cart>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['cart', key],
    // One cart mutation at a time keeps versions monotonic.
    scope: { id: 'cart' },
    mutationFn: request,
    onSuccess: (cart) => {
      queryClient.setQueryData(cartKeys.current(), cart);
    },
  });
}

export function useAddCartLines() {
  return useCartMutation('add-lines', (command: AddCartLinesCommand) =>
    browserApiRequest('/api/cart/lines', CartSchema, { method: 'POST', body: json(command) }),
  );
}

export function useUpdateCartLine() {
  return useCartMutation(
    'update-line',
    ({ lineId, ...command }: UpdateCartLineCommand & { lineId: string }) =>
      browserApiRequest(linePath(lineId), CartSchema, { method: 'PATCH', body: json(command) }),
  );
}

export function useRemoveCartLine() {
  return useCartMutation('remove-line', ({ lineId }: { lineId: string }) =>
    browserApiRequest(linePath(lineId), CartSchema, { method: 'DELETE' }),
  );
}

export function useApplyPromo() {
  return useCartMutation('apply-promo', ({ code }: { code: string }) =>
    browserApiRequest('/api/cart/promo', CartSchema, { method: 'POST', body: json({ code }) }),
  );
}

export function useRemovePromo() {
  return useCartMutation('remove-promo', () =>
    browserApiRequest('/api/cart/promo', CartSchema, { method: 'DELETE' }),
  );
}

export function useReorder() {
  return useCartMutation('reorder', ({ orderId }: { orderId: string }) =>
    browserApiRequest('/api/cart/reorder', CartSchema, { method: 'POST', body: json({ orderId }) }),
  );
}
